import './new-manual-import.dialog.scss'
import * as angular from "angular";
import {IPromise, IScope} from "angular";
import {
    ManualImportServiceName,
    ToastNotificationServiceName,
    UserServiceName
} from "../../../utils/names/serviceNames";
import {ManualImportService} from "../../services/ManualImportService";
import {ToastNotificationService} from "../../../utils/ToastNotificationService";
import {ImportLogConfigs} from "../../model/ImportLogConfigs";
import {ImportSource} from "../../model/ImportSource";
import {PrincipalUtil} from "../../../utils/PrincipalUtil";
import {UserGranredAuthorityName} from "../../../router/const/AllPermitions";
import {UserView} from "../../../users/model/UserFilter";
import {UserService} from "../../../users_service/service/user.service";
import {UserDto} from "../../../users/model/User";

export interface ManualImportDialogScope extends IScope {
    showLoader: boolean;
    configs: ImportLogConfigs
    submitImport: () => void;
    closeDialog: () => void;
    disableSubmitImport: boolean;
    allowLeadImport: boolean;
    allowPartnerImport: boolean;
    setForm: (importSource: ImportSource, form: angular.INgModelController) => void;
    updateFile: () => void;
    updateLeadFile: () => void;
    updatePartnerFile: () => void;
    updateImportSource: (importSource: ImportSource) => void;
    employee: UserDto;
    queryEmployee: (searchText: string) => IPromise<UserDto[]>;
    onSelectedEmployeeChange: (item: UserDto) => void;
}

export class NewManualImportDialog {

    static instance = ({
        controller: [
            '$scope',
            '$mdDialog',
            ManualImportServiceName,
            UserServiceName,
            ToastNotificationServiceName,
            NewManualImportDialog
        ],
        template: require('./new-manual-import.dialog.html'),
    });

    private formMap: Map<ImportSource, angular.INgModelController> = new Map<ImportSource, angular.INgModelController>();
    private validatorsMap: Map<ImportSource, () => boolean>;
    private file: File;
    private employee: UserDto;

    constructor(private $scope: ManualImportDialogScope,
                private $mdDialog: angular.material.IDialogService,
                private manualImportService: ManualImportService,
                private employeeService: UserService,
                private toastNotficationService: ToastNotificationService) {
        this.$scope.configs = {
            importSource: null
        };
        this.$scope.submitImport = this.submitImport;
        this.$scope.disableSubmitImport = true;
        this.$scope.closeDialog = this.closeDialog;
        this.$scope.updateFile = this.updateFile;
        this.$scope.updateLeadFile = this.updateLeadFile;
        this.$scope.updatePartnerFile = this.updatePartnerFile;
        this.$scope.updateImportSource = this.updateImportSource;
        this.$scope.queryEmployee = this.queryEmployee;
        this.$scope.onSelectedEmployeeChange = this.onSelectedEmployeeChange;
        this.$scope.setForm = this.setForm;
        this.$scope.employee = null;
        this.validatorsMap = this.buildValidatorsMap();
        this.$scope.$watch('configs', this.watchForSubmitImport, true);
        this.$scope.$watch('employee', this.watchForSubmitImport, true);
        this.initLeadFeatureAccessAllowed();
        this.initPartnerFeatureAccessAllowed();
    }

    private initPartnerFeatureAccessAllowed() {
        PrincipalUtil.hasAuthorityPromise(UserGranredAuthorityName.PARTNERS)
            .then(status => this.$scope.allowPartnerImport = status);
    }
    private initLeadFeatureAccessAllowed() {
        PrincipalUtil.hasAuthorityPromise(UserGranredAuthorityName.LEADS)
            .then(status => this.$scope.allowLeadImport = status);
    }

    private buildValidatorsMap() {
        let map = new Map<ImportSource, () => boolean>();
        for (let importSourceKey in ImportSource) {
            map.set(<ImportSource>importSourceKey, () => true);
        }
        map.set(ImportSource.CSV_TEMPLATE, () => {
            let csvFile = this.$scope.configs.csvFile;
            return csvFile != null && csvFile.name.endsWith(".csv") && csvFile.importSource == ImportSource.CSV_TEMPLATE;
        });
        map.set(ImportSource.CSV_LEADS, () => {
            let csvFile = this.$scope.configs.csvFile;
            return csvFile != null && csvFile.name.endsWith(".csv") && csvFile.importSource == ImportSource.CSV_LEADS && this.$scope.employee != undefined;
        });
        map.set(ImportSource.CSV_PARTNERS, () => {
            let csvFile = this.$scope.configs.csvFile;
            return csvFile != null && csvFile.name.endsWith(".csv") && csvFile.importSource == ImportSource.CSV_PARTNERS && this.$scope.employee != undefined;
        });
        return map;
    }

    private setForm = (importSource: ImportSource, form: angular.INgModelController) => {
        this.formMap.set(importSource, form);
    };

    private submitImport = () => {
        this.showLoader()
            .then(() => this.manualImportService.executeManualImport(this.$scope.configs, this.file, this.$scope.employee == undefined ? null : this.$scope.employee.id))
            .then(this.handleResponse);
    };

    private showLoader = ():IPromise<any> => {
        this.$scope.disableSubmitImport = true;
        this.$scope.showLoader = true;
        return new Promise(resolve => {
            resolve();
        });
    };

    private handleResponse = (resp: angular.IHttpResponse<any>) => {
        let title = this.$scope.configs.importSource == undefined || this.$scope.configs.importSource == ImportSource.LQB ? this.$scope.configs.title : this.$scope.configs.csvFile.name;
        if (resp.status === 200) {
            this.toastNotficationService.generateSuccessMessage("Executing new import " + title);
            this.$mdDialog.hide(this.$scope.configs);
        } else if (resp.status === 400) {
            this.toastNotficationService.generateErrorMessage(resp.data.message);
            this.$mdDialog.cancel(this.$scope.configs);
        } else {
            this.toastNotficationService.generateErrorMessage("Failed to execute report " + title);
            this.$mdDialog.cancel(this.$scope.configs);
        }
    };

    private watchForSubmitImport = (newValue: ManualImportDialogScope, oldValue: ManualImportDialogScope, $scope: ManualImportDialogScope) => {
        let form = this.formMap.get($scope.configs.importSource);
        if (form == null) {
            this.$scope.disableSubmitImport = true;
        } else {
            let customValidator = this.validatorsMap.get(this.$scope.configs.importSource);
            this.$scope.disableSubmitImport = form.$invalid || !customValidator();
        }
    };

    private closeDialog = () => {
        this.$mdDialog.cancel();
    };

    updateFile = () => {
        let fileInput = <HTMLInputElement>document.getElementById('csv-file-input');
        let file = fileInput.files[0];
        if (file) {
            this.$scope.configs.csvFile = {
                importSource: ImportSource.CSV_TEMPLATE,
                name: file.name,
                type: file.type
            };
            this.file = file;
            this.$scope.$apply();
        }
    };

    updateLeadFile = () => {
        let fileInput = <HTMLInputElement>document.getElementById('csv-lead-file-input');
        let file = fileInput.files[0];
        if (file) {
            this.$scope.configs.csvFile = {
                importSource: ImportSource.CSV_LEADS,
                name: file.name,
                type: file.type
            };
            this.file = file;
            this.$scope.$apply();
        }
    };

    updatePartnerFile = () => {
        let fileInput = <HTMLInputElement>document.getElementById('csv-partner-file-input');
        let file = fileInput.files[0];
        if (file) {
            this.$scope.configs.csvFile = {
                importSource : ImportSource.CSV_PARTNERS,
                name: file.name,
                type: file.type
            };
            this.file = file;
            this.$scope.$apply();
        }
    };

    updateImportSource = (importSource: ImportSource) => {
        this.$scope.configs.importSource = importSource;
    };

    onSelectedEmployeeChange = (item: UserDto) => {
        this.$scope.employee = item;
        console.log("user was changed " + item.firstName + " " + item.lastName);
    };

    queryEmployee = (query: string) => {
        return this.employeeService.queryEmployee({
            userIds: [],
            branchesIds: [],
            showDeleted: true,
            showDisabled: false,
            view: UserView.AUTOCOMPLETE,
            query: query
        }).then((resp) => {
            if (resp.status === 200) {
                return resp.data;
            } else {
                this.toastNotficationService.generateErrorMessage('Failed to fetch users information!')
            }
        })
    };

}